/* eslint-disable react/require-default-props */
import React from 'react';
import {
  ActivityIndicator,
  Modal,
  PixelRatio,
  Platform,
  Pressable,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {useBottomSheetModal} from './BottomSheetModal.hook';
import {styles} from './BottomSheetModal.style';
import type {BottomSheetModalProps} from './BottomSheetModal.types';

const {bottomSheet} = ManageMySiteTestIDs;

const Backdrop = ({onClose}: {onClose: () => void}) => {
  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    return (
      <TouchableWithoutFeedback onPress={onClose} accessible={false}>
        <View
          style={styles.backdrop}
          testID={bottomSheet.backdrop}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
          pointerEvents="box-only"
        />
      </TouchableWithoutFeedback>
    );
  }

  return (
    <Pressable
      onPress={onClose}
      style={styles.backdrop}
      testID={bottomSheet.backdrop}
      accessible={false}
      focusable={false}
      importantForAccessibility="no"
    />
  );
};

const Footer = ({
  onClearAll,
  onSave,
  canSave,
  fullWidthSave,
  isSaving = false,
  isClearAllDisabled = false,
  isLandscape = false,
}: {
  onClearAll: (() => void) | null;
  onSave: (() => void) | null;
  canSave: boolean;
  fullWidthSave: boolean;
  isSaving: boolean;
  isClearAllDisabled?: boolean;
  isLandscape?: boolean;
}) => {
  const insets = useSafeAreaInsets();
  const isSaveDisabled = !canSave || isSaving;

  if (!onClearAll && !onSave) {
    return null;
  }

  return (
    <View
      style={[
        styles.footer,
        fullWidthSave && !onClearAll && styles.footerFullWidth,
        Platform.OS !== 'web' && {paddingBottom: insets.bottom + 8},
        isLandscape && {paddingTop: 12},
        isLandscape &&
          !(fullWidthSave && !onClearAll) && {
            justifyContent: 'center',
            gap: 16,
          },
      ]}
      accessibilityLabel="Bottom sheet footer">
      {onClearAll && (
        <TouchableOpacity
          style={[styles.clearButton, isClearAllDisabled && {opacity: 0.5}]}
          onPress={isClearAllDisabled ? undefined : onClearAll}
          disabled={isClearAllDisabled}
          accessibilityRole="button"
          accessibilityLabel="Clear all"
          accessibilityState={{disabled: isClearAllDisabled}}
          accessibilityHint={
            isClearAllDisabled
              ? 'Please wait while changes are being saved.'
              : undefined
          }
          testID={bottomSheet.clearAll}>
          <Text
            style={styles.clearButtonText}
            allowFontScaling
            maxFontSizeMultiplier={2}>
            {tt('clearAll', 'Clear All')}
          </Text>
        </TouchableOpacity>
      )}

      {onSave && (
        <TouchableOpacity
          style={[
            styles.saveButton,
            fullWidthSave && !onClearAll && styles.saveButtonFullWidth,
            isSaveDisabled && styles.saveButtonDisabled,
            Platform.OS === 'web' && fullWidthSave
              ? {width: '100%'}
              : fullWidthSave && !onClearAll && isLandscape && {width: '60%'},
          ]}
          onPress={isSaveDisabled ? undefined : onSave}
          disabled={isSaveDisabled}
          accessible
          accessibilityRole="button"
          accessibilityLabel={isSaveDisabled ? 'Save, disabled' : 'Save'}
          accessibilityState={{disabled: isSaveDisabled}}
          accessibilityHint={
            isSaveDisabled
              ? 'Complete required fields to enable Save.'
              : undefined
          }
          testID={bottomSheet.save}>
          {isSaving ? (
            <ActivityIndicator
              size="small"
              color={styles.saveButtonTextDisabled.color}
            />
          ) : (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={[
                styles.saveButtonText,
                isSaveDisabled && styles.saveButtonTextDisabled,
              ]}
              accessible={false}>
              {tt('save', 'Save')}
            </Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

export const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  visible,
  title,
  onClose,
  onClearAll = null,
  onSave = null,
  canSave = true,
  scrollable = false,
  children,
  fullWidthSave = false,
  isSaving = false,
  isClearAllDisabled = false,
}) => {
  const insets = useSafeAreaInsets();
  const {
    keyboardHeight,
    screenHeight,
    screenWidth,
    closeButtonKeyboardRef,
    closeButtonA11yRef,
    focusCloseButton,
  } = useBottomSheetModal();

  const isLandscape = screenWidth > screenHeight;

  const fontScale = PixelRatio.getFontScale();
  const scaledCloseIconSize = fontScale > 1.3 ? 24 : 22;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={
        Platform.OS === 'web'
          ? 'fade'
          : Platform.OS === 'android'
          ? 'fade'
          : 'slide'
      }
      statusBarTranslucent
      onRequestClose={onClose}
      onShow={focusCloseButton}
      supportedOrientations={[
        'portrait',
        'portrait-upside-down',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}>
      <View style={styles.modalRoot} accessibilityViewIsModal>
        <Backdrop onClose={onClose} />

        <View
          style={[
            styles.keyboardSpacer,
            {
              paddingBottom:
                Platform.OS === 'android'
                  ? keyboardHeight > 0
                    ? keyboardHeight + 20
                    : 0
                  : 0,
            },
          ]}
          pointerEvents="box-none">
          <View
            testID={bottomSheet.card}
            // accessibilityViewIsModal
            importantForAccessibility="yes"
            style={[
              styles.sheetCard,
              Platform.OS !== 'web' && {
                width: screenWidth,
                maxHeight:
                  keyboardHeight > 0
                    ? screenHeight - keyboardHeight - 24
                    : isLandscape
                    ? screenHeight * 0.75
                    : screenHeight * 0.85,
              },
            ]}>
            <View
              style={styles.headerRow}
              collapsable={false}
              accessible={false}
              ref={closeButtonA11yRef}>
              <KeyboardFocusable
                ref={closeButtonKeyboardRef}
                accessible
                accessibilityRole="button"
                accessibilityLabel={tt('close', 'Close')}
                style={[
                  styles.closeButton,
                  Platform.OS === 'android' &&
                    isLandscape && {marginLeft: insets.left},
                ]}
                onPress={onClose}
                testID={bottomSheet.closeButtonContainer}>
                <Icon
                  name="close"
                  style={[styles.closeIcon, {fontSize: scaledCloseIconSize}]}
                  testID={bottomSheet.closeButton}
                />
              </KeyboardFocusable>

              <KeyboardFocusable
                accessible
                accessibilityLabel={title}
                accessibilityRole="header"
                testID={bottomSheet.title}
                style={{flexShrink: 1}}>
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={1.5}
                  testID={bottomSheet.titleText}
                  isBold
                  style={styles.headingText}
                  accessible={false}>
                  {title}
                </Text>
              </KeyboardFocusable>
            </View>

            <View style={styles.divider} />

            {scrollable ? (
              <ScrollView
                style={[
                  styles.body,
                  Platform.OS === 'android' &&
                    isLandscape && {paddingLeft: 20 + insets.left},
                ]}
                contentContainerStyle={styles.bodyContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                bounces={false}>
                {children}
              </ScrollView>
            ) : (
              <View
                style={[
                  styles.body,
                  styles.bodyContent,
                  Platform.OS === 'android' &&
                    isLandscape && {paddingLeft: 20 + insets.left},
                ]}>
                {children}
              </View>
            )}

            <Footer
              onClearAll={onClearAll}
              onSave={onSave}
              canSave={canSave}
              fullWidthSave={fullWidthSave}
              isSaving={isSaving}
              isClearAllDisabled={isClearAllDisabled}
              isLandscape={isLandscape}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default BottomSheetModal;
